import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const TikTokContext = createContext();

export const useTikTok = () => useContext(TikTokContext);

const TIKTOK_CLIENT_KEY = import.meta.env.VITE_TIKTOK_CLIENT_KEY;
const TIKTOK_REDIRECT_URI = import.meta.env.VITE_TIKTOK_REDIRECT_URI;
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

// TikTok OAuth scopes
// Only scopes APPROVED on the TikTok production app. Requesting an unapproved
// scope fails the whole authorize call with "scope" and no indication of which
// one -- so this list must track the portal, not what we eventually want.
//
// video.list (Display API) is not approved. Nothing reads it today; add it back
// here once it is granted.
const SCOPES = [
  'user.info.basic',
  'user.info.profile',
  'user.info.stats',
  'video.upload',   // Upload to drafts/inbox
  'video.publish',  // Direct publish
].join(',');

// Generate a cryptographically secure random string
const generateRandomString = (length = 43) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  // Use base64url encoding (RFC 4648)
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .substring(0, length);
};

// Generate SHA256 hash for PKCE code challenge
const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  // Convert to base64url
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const TikTokProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth?.user);

  // Load connected accounts from backend on mount
  useEffect(() => {
    if (user) {
      loadConnectedAccounts();
    } else {
      setAccounts([]);
      setLoading(false);
    }
  }, [user]);

  const loadConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to load TikTok accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Start TikTok OAuth flow
  const connectTikTok = useCallback(async () => {
    try {
      // Generate CSRF state token
      const state = generateRandomString(32);
      sessionStorage.setItem('tiktok_oauth_state', state);

      // Generate PKCE code verifier (43-128 chars)
      const codeVerifier = generateRandomString(64);
      sessionStorage.setItem('tiktok_code_verifier', codeVerifier);

      // Generate code challenge (SHA256 hash of verifier)
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Build authorization URL
      const authUrl = new URL('https://www.tiktok.com/v2/auth/authorize/');
      authUrl.searchParams.set('client_key', TIKTOK_CLIENT_KEY);
      authUrl.searchParams.set('scope', SCOPES);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', TIKTOK_REDIRECT_URI);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');

      // Redirect to TikTok
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Failed to start TikTok OAuth:', err);
      setError('Failed to start TikTok connection');
    }
  }, []);

  // Handle OAuth callback
  const handleCallback = useCallback(async (code, state) => {
    // Validate state parameter
    const savedState = sessionStorage.getItem('tiktok_oauth_state');
    if (!savedState || state !== savedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // Get code verifier for PKCE
    const codeVerifier = sessionStorage.getItem('tiktok_code_verifier');
    if (!codeVerifier) {
      throw new Error('Missing code verifier - please try connecting again');
    }

    try {
      // Exchange code for tokens via backend
      const response = await fetch(`${API_BASE_URL}/tiktok/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          code,
          code_verifier: codeVerifier,
          redirect_uri: TIKTOK_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect TikTok account');
      }

      const data = await response.json();

      // Update accounts list. The backend keys accounts by (open_id, org), so
      // re-connecting an account already linked to this org returns the SAME
      // account (already_connected=true) rather than a new one — dedupe by id so
      // it doesn't appear twice. This commonly happens when the browser is still
      // logged into the platform: TikTok's OAuth has no way to force the account
      // chooser, so it silently re-authorizes the same account. To add a
      // DIFFERENT account, log out of tiktok.com or use an incognito window.
      setAccounts(prev => {
        const exists = prev.some(a => a.id === data.account.id);
        return exists
          ? prev.map(a => (a.id === data.account.id ? data.account : a))
          : [...prev, data.account];
      });

      // Clean up session storage
      sessionStorage.removeItem('tiktok_oauth_state');
      sessionStorage.removeItem('tiktok_code_verifier');

      return { account: data.account, alreadyConnected: !!data.already_connected };
    } catch (err) {
      // Clean up on error too
      sessionStorage.removeItem('tiktok_oauth_state');
      sessionStorage.removeItem('tiktok_code_verifier');
      throw err;
    }
  }, []);

  // Disconnect an account
  const disconnectAccount = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setAccounts(prev => prev.filter(a => a.id !== accountId));
      } else {
        throw new Error('Failed to disconnect account');
      }
    } catch (err) {
      console.error('Failed to disconnect account:', err);
      setError(err.message);
    }
  }, []);

  // Refresh account stats
  const refreshAccountStats = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, ...data.account } : a))
        );
        return data.account;
      } else {
        throw new Error('Failed to refresh stats');
      }
    } catch (err) {
      console.error('Failed to refresh account stats:', err);
      setError(err.message);
    }
  }, []);

  // Fetch videos for an account
  const fetchAccountVideos = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}/videos`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch videos');
      }
    } catch (err) {
      console.error('Failed to fetch account videos:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Get account by ID
  const getAccountById = useCallback((accountId) => {
    return accounts.find(a => a.id === accountId);
  }, [accounts]);

  // Get creator info for posting
  const getCreatorInfo = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}/creator-info`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return data.creator_info;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get creator info');
      }
    } catch (err) {
      console.error('Failed to get creator info:', err);
      throw err;
    }
  }, []);

  // Initialize video upload (returns upload_url for FILE_UPLOAD)
  const initVideoPost = useCallback(async (accountId, options) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}/post/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(options),
      });

      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize video post');
      }
    } catch (err) {
      console.error('Failed to init video post:', err);
      throw err;
    }
  }, []);

  // Check post status
  const checkPostStatus = useCallback(async (accountId, publishId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok/accounts/${accountId}/post/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ publish_id: publishId }),
      });

      if (response.ok) {
        return await response.json();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check post status');
      }
    } catch (err) {
      console.error('Failed to check post status:', err);
      throw err;
    }
  }, []);

  // Upload video file to TikTok's upload_url
  const uploadVideoFile = useCallback(async (uploadUrl, file, onProgress) => {
    try {
      const chunkSize = 10 * 1024 * 1024; // 10MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'video/mp4',
            'Content-Length': chunk.size.toString(),
            'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
          },
          body: chunk,
        });

        if (!response.ok) {
          throw new Error(`Upload failed at chunk ${i + 1}/${totalChunks}`);
        }

        if (onProgress) {
          onProgress(Math.round(((i + 1) / totalChunks) * 100));
        }
      }

      return true;
    } catch (err) {
      console.error('Failed to upload video file:', err);
      throw err;
    }
  }, []);

  return (
    <TikTokContext.Provider
      value={{
        accounts,
        loading,
        error,
        connectTikTok,
        handleCallback,
        disconnectAccount,
        refreshAccountStats,
        loadConnectedAccounts,
        fetchAccountVideos,
        getAccountById,
        getCreatorInfo,
        initVideoPost,
        checkPostStatus,
        uploadVideoFile,
      }}
    >
      {children}
    </TikTokContext.Provider>
  );
};

export default TikTokContext;
